import { Transition } from "@headlessui/react"
import { Form, Head, router, usePage } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"

import DeleteUser from "@/components/delete-user"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsProfilePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Profile settings",
    href: settingsProfilePath(),
  },
]

interface ProfileProps {
  time_zones: string[]
}

export default function Profile({ time_zones: timeZones }: ProfileProps) {
  const { auth } = usePage().props
  const detectedTimeZone =
    typeof window === "undefined"
      ? ""
      : Intl.DateTimeFormat().resolvedOptions().timeZone
  const initialTimeZone = timeZones.includes(detectedTimeZone)
    ? detectedTimeZone
    : ""
  const [timeZone, setTimeZone] = useState(
    auth.user.time_zone ?? initialTimeZone,
  )
  const initializationRequested = useRef(false)

  useEffect(() => {
    if (auth.user.time_zone) return
    if (initializationRequested.current) return
    if (!detectedTimeZone || !timeZones.includes(detectedTimeZone)) return

    initializationRequested.current = true
    router.patch(
      settingsProfilePath(),
      { browser_time_zone: detectedTimeZone },
      { preserveScroll: true, preserveState: true },
    )
  }, [auth.user.time_zone, detectedTimeZone, timeZones])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Profile information"
            description="Update your name and calendar time zone"
          />

          <Form
            method="patch"
            action={settingsProfilePath()}
            options={{
              preserveScroll: true,
            }}
            className="space-y-6"
          >
            {({ errors, processing, recentlySuccessful }) => (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="name">Name</Label>

                  <Input
                    id="name"
                    name="name"
                    className="mt-1 block w-full"
                    defaultValue={auth.user.name}
                    required
                    autoComplete="name"
                    placeholder="Full name"
                  />

                  <InputError className="mt-2" messages={errors.name} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="time_zone">Time zone</Label>

                  <Input
                    id="time_zone"
                    name="time_zone"
                    list="time-zone-options"
                    value={timeZone}
                    onChange={(event) => setTimeZone(event.target.value)}
                    required
                    autoComplete="off"
                    placeholder="America/Los_Angeles"
                  />
                  <datalist id="time-zone-options">
                    {timeZones.map((zone) => (
                      <option key={zone} value={zone} />
                    ))}
                  </datalist>

                  <p className="text-sm text-neutral-600">
                    Used for Monday–Sunday weeks and calendar-year reports.
                  </p>
                  <InputError className="mt-2" messages={errors.time_zone} />
                </div>

                <div className="flex items-center gap-4">
                  <Button disabled={processing}>Save</Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-sm text-neutral-600">Saved</p>
                  </Transition>
                </div>
              </>
            )}
          </Form>
        </div>

        <DeleteUser />
      </SettingsLayout>
    </AppLayout>
  )
}
